import React, { useState } from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, SparklesIcon, AlertCircleIcon } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, ComposedChart } from 'recharts';
const SalesForecastingDashboard: React.FC = () => {
  const [selectedScenario, setSelectedScenario] = useState('Base');
  const kpis = [{
    title: 'Forecast Accuracy %',
    value: '92.4%',
    change: '+2.1%',
    isPositive: true,
    subtitle: 'Last 6 months avg'
  }, {
    title: 'Next 3-Month Projection',
    value: '₹2,485 Cr',
    change: '+8.2%',
    isPositive: true,
    subtitle: 'Base scenario'
  }, {
    title: 'Demand Variability Index',
    value: '14.2',
    change: '-1.5 pts',
    isPositive: true,
    subtitle: 'Lower is better'
  }];
  const forecastVsActualData = [{
    month: 'Jan',
    actual: 720,
    forecast: 735,
    accuracy: 97.9
  }, {
    month: 'Feb',
    actual: 745,
    forecast: 752,
    accuracy: 99.1
  }, {
    month: 'Mar',
    actual: 768,
    forecast: 742,
    accuracy: 96.6
  }, {
    month: 'Apr',
    actual: 752,
    forecast: 765,
    accuracy: 98.3
  }, {
    month: 'May',
    actual: 785,
    forecast: 778,
    accuracy: 99.1
  }, {
    month: 'Jun',
    actual: 802,
    forecast: 815,
    accuracy: 98.4
  }, {
    month: 'Jul',
    actual: 795,
    forecast: 788,
    accuracy: 99.1
  }, {
    month: 'Aug',
    actual: 820,
    forecast: 802,
    accuracy: 97.8
  }, {
    month: 'Sep',
    actual: 812,
    forecast: 825,
    accuracy: 98.4
  }, {
    month: 'Oct',
    forecast: 835,
    optimistic: 895,
    pessimistic: 775
  }, {
    month: 'Nov',
    forecast: 848,
    optimistic: 920,
    pessimistic: 785
  }, {
    month: 'Dec',
    forecast: 862,
    optimistic: 945,
    pessimistic: 795
  }];
  const scenarioData = {
    Optimistic: {
      projection: '₹2,760 Cr',
      growth: '+15.2%',
      assumptions: ['Strong festive season demand', 'New EV model launches on schedule', 'Market share gain of 1.5%', 'No supply chain disruptions'],
      color: 'from-green-500 to-emerald-600'
    },
    Base: {
      projection: '₹2,485 Cr',
      growth: '+8.2%',
      assumptions: ['Moderate festive demand', 'Current growth trajectory continues', 'Market share stable', 'Minor supply variations'],
      color: 'from-blue-500 to-indigo-600'
    },
    Pessimistic: {
      projection: '₹2,355 Cr',
      growth: '+2.8%',
      assumptions: ['Weak consumer sentiment', 'Delayed new model launches', 'Increased competition pressure', 'Material cost inflation'],
      color: 'from-red-500 to-rose-600'
    }
  };
  const skuForecastData = [{
    sku: 'LED Headlamp Assembly',
    category: 'Lighting',
    oct: 72,
    nov: 75,
    dec: 78,
    q4Total: 225,
    confidence: 'High'
  }, {
    sku: 'Tail Lamp Complete',
    category: 'Lighting',
    oct: 68,
    nov: 71,
    dec: 73,
    q4Total: 212,
    confidence: 'High'
  }, {
    sku: 'Bumper Assembly',
    category: 'Polymer',
    oct: 58,
    nov: 62,
    dec: 65,
    q4Total: 185,
    confidence: 'Medium'
  }, {
    sku: 'Door Handle Set',
    category: 'Polymer',
    oct: 52,
    nov: 54,
    dec: 56,
    q4Total: 162,
    confidence: 'High'
  }, {
    sku: 'Wiring Harness',
    category: 'Electrical',
    oct: 85,
    nov: 88,
    dec: 92,
    q4Total: 265,
    confidence: 'Medium'
  }, {
    sku: 'ECU Module',
    category: 'Electrical',
    oct: 48,
    nov: 51,
    dec: 53,
    q4Total: 152,
    confidence: 'High'
  }, {
    sku: 'Airbag Assembly',
    category: 'Safety',
    oct: 38,
    nov: 40,
    dec: 42,
    q4Total: 120,
    confidence: 'Medium'
  }, {
    sku: 'Seat Belt System',
    category: 'Safety',
    oct: 42,
    nov: 44,
    dec: 46,
    q4Total: 132,
    confidence: 'High'
  }];
  const accuracyTrendData = [{
    month: 'Apr',
    accuracy: 91.2
  }, {
    month: 'May',
    accuracy: 92.8
  }, {
    month: 'Jun',
    accuracy: 90.5
  }, {
    month: 'Jul',
    accuracy: 93.2
  }, {
    month: 'Aug',
    accuracy: 91.8
  }, {
    month: 'Sep',
    accuracy: 94.1
  }];
  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'High':
        return 'text-green-600 bg-green-50';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'Low':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Lighting':
        return 'bg-blue-100 text-blue-800';
      case 'Polymer':
        return 'bg-green-100 text-green-800';
      case 'Electrical':
        return 'bg-yellow-100 text-yellow-800';
      case 'Safety':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div data-id="element-9098">
      <DescriptionPanel title="Sales Forecasting & Demand Planning" description="AI-powered sales forecasting with scenario modeling and SKU-level projections. Track forecast accuracy, analyze demand patterns, and plan inventory allocation based on predictive analytics." insights={['Forecast accuracy improved to 92.4% - 2.1% better than last quarter', 'Next 3 months projected at ₹2,485 Cr with 8.2% growth in base scenario', 'Lighting category showing highest forecast confidence at 95%+', 'Demand variability index reduced to 14.2 - improved planning stability']} data-id="element-9099" />
      {/* KPI Tiles */}
      <div className="mb-8" data-id="element-9100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-9101">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-9102">
              <div className="flex flex-col h-full" data-id="element-9103">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-9104">
                  {kpi.title}
                </div>
                <div className="text-xs text-gray-400 mb-3" data-id="element-9105">{kpi.subtitle}</div>
                <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-9106">
                  {kpi.value}
                </div>
                <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-9107">
                  {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-9108" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-9109" />}
                  {kpi.change} vs last period
                </div>
              </div>
            </Card>)}
        </div>
      </div>
      {/* Forecast vs Actual Chart */}
      <Card className="p-6 mb-8" data-id="element-9110">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9111">
          Forecast vs Actual Revenue Trend
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-9112">
          Historical performance and 3-month projection (₹ Cr)
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-9113">
          <ComposedChart data={forecastVsActualData} data-id="element-9114">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-9115" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-9116" />
            <YAxis yAxisId="left" tick={{
            fontSize: 12
          }} data-id="element-9117" />
            <YAxis yAxisId="right" orientation="right" tick={{
            fontSize: 12
          }} domain={[90, 100]} data-id="element-9118" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} formatter={(value: any, name: string) => {
            if (name === 'Accuracy') return `${value}%`;
            return `₹${value} Cr`;
          }} data-id="element-9119" />
            <Legend data-id="element-9120" />
            <Line yAxisId="left" type="monotone" dataKey="actual" stroke="#3b82f6" strokeWidth={3} name="Actual" dot={{
            r: 5,
            strokeWidth: 2
          }} data-id="element-9121" />
            <Line yAxisId="left" type="monotone" dataKey="forecast" stroke="#10b981" strokeWidth={3} strokeDasharray="5 5" name="Forecast" dot={{
            r: 5,
            strokeWidth: 2
          }} data-id="element-9122" />
            <Line yAxisId="left" type="monotone" dataKey="optimistic" stroke="#22c55e" strokeWidth={2} strokeDasharray="3 3" name="Optimistic" dot={false} data-id="element-9123" />
            <Line yAxisId="left" type="monotone" dataKey="pessimistic" stroke="#ef4444" strokeWidth={2} strokeDasharray="3 3" name="Pessimistic" dot={false} data-id="element-9124" />
            <Bar yAxisId="right" dataKey="accuracy" fill="#f59e0b" opacity={0.3} name="Accuracy" data-id="element-9125" />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
      {/* Scenario Selector and AI Forecast Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-9126">
        {/* Scenario Cards */}
        {Object.entries(scenarioData).map(([scenario, data]) => <Card key={scenario} className={`p-6 cursor-pointer transition-all ${selectedScenario === scenario ? `bg-gradient-to-br ${data.color} text-white shadow-lg scale-105` : 'hover:shadow-md'}`} onClick={() => setSelectedScenario(scenario)} data-id="element-9127">
            <div className="flex items-center justify-between mb-3" data-id="element-9128">
              <h3 className={`text-lg font-bold ${selectedScenario === scenario ? 'text-white' : 'text-gray-800'}`} data-id="element-9129">
                {scenario} Scenario
              </h3>
              {selectedScenario === scenario && <div className="w-3 h-3 rounded-full bg-white animate-pulse" data-id="element-9130" />}
            </div>
            <div className={`text-3xl font-bold mb-2 ${selectedScenario === scenario ? 'text-white' : 'text-gray-900'}`} data-id="element-9131">
              {data.projection}
            </div>
            <div className={`text-sm font-semibold mb-4 ${selectedScenario === scenario ? 'text-white/90' : 'text-green-600'}`} data-id="element-9132">
              {data.growth} growth
            </div>
            <div className="space-y-2" data-id="element-9133">
              {data.assumptions.map((assumption, index) => <div key={index} className="flex items-start" data-id="element-9134">
                  <span className={`mr-2 ${selectedScenario === scenario ? 'text-white/80' : 'text-blue-600'}`} data-id="element-9135">
                    •
                  </span>
                  <span className={`text-xs ${selectedScenario === scenario ? 'text-white/90' : 'text-gray-600'}`} data-id="element-9136">
                    {assumption}
                  </span>
                </div>)}
            </div>
          </Card>)}
      </div>
      {/* AI Forecast Input Panel and Accuracy Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-9137">
        <Card className="p-6 bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200" data-id="element-9138">
          <div className="flex items-center mb-4" data-id="element-9139">
            <SparklesIcon size={24} className="text-purple-600 mr-2" data-id="element-9140" />
            <h3 className="text-lg font-semibold text-gray-800" data-id="element-9141">
              AI Forecast Inputs
            </h3>
          </div>
          <div className="space-y-4" data-id="element-9142">
            <div className="p-4 bg-white rounded-lg border border-purple-100" data-id="element-9143">
              <div className="flex items-center justify-between mb-2" data-id="element-9144">
                <span className="text-sm font-medium text-gray-700" data-id="element-9145">
                  Historical Data Quality
                </span>
                <span className="text-sm font-bold text-green-600" data-id="element-9146">98.5%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-9147">
                <div className="h-full bg-green-500" style={{
                width: '98.5%'
              }} data-id="element-9148" />
              </div>
            </div>
            <div className="p-4 bg-white rounded-lg border border-purple-100" data-id="element-9149">
              <div className="flex items-center justify-between mb-2" data-id="element-9150">
                <span className="text-sm font-medium text-gray-700" data-id="element-9151">
                  External Data Integration
                </span>
                <span className="text-sm font-bold text-blue-600" data-id="element-9152">Active</span>
              </div>
              <div className="text-xs text-gray-600 space-y-1" data-id="element-9153">
                <div data-id="element-9154">• Market sentiment indicators</div>
                <div data-id="element-9155">• Competitor pricing data</div>
                <div data-id="element-9156">• Economic indices (GDP, PMI)</div>
              </div>
            </div>
            <div className="p-4 bg-white rounded-lg border border-purple-100" data-id="element-9157">
              <div className="flex items-center justify-between mb-2" data-id="element-9158">
                <span className="text-sm font-medium text-gray-700" data-id="element-9159">
                  Model Confidence
                </span>
                <span className="text-sm font-bold text-purple-600" data-id="element-9160">High</span>
              </div>
              <div className="text-xs text-gray-600" data-id="element-9161">
                Random Forest + ARIMA ensemble model with 92.4% accuracy over 6
                months
              </div>
            </div>
            <button className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all" data-id="element-9162">
              Regenerate Forecast with Latest Data
            </button>
          </div>
        </Card>
        <Card className="p-6" data-id="element-9163">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-9164">
            Forecast Accuracy Trend (%)
          </h3>
          <ResponsiveContainer width="100%" height={280} data-id="element-9165">
            <LineChart data={accuracyTrendData} data-id="element-9166">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-9167" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-9168" />
              <YAxis domain={[88, 96]} tick={{
              fontSize: 12
            }} data-id="element-9169" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value}%`} data-id="element-9170" />
              <Line type="monotone" dataKey="accuracy" stroke="#3b82f6" strokeWidth={3} dot={{
              r: 6,
              strokeWidth: 2,
              fill: '#3b82f6'
            }} data-id="element-9171" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200" data-id="element-9172">
            <div className="flex items-center" data-id="element-9173">
              <AlertCircleIcon size={16} className="text-green-600 mr-2" data-id="element-9174" />
              <span className="text-sm text-green-800 font-medium" data-id="element-9175">
                Accuracy improving month-over-month - model learning from new
                patterns
              </span>
            </div>
          </div>
        </Card>
      </div>
      {/* SKU-Level Forecast Table */}
      <Card className="p-6" data-id="element-9176">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-9177">
          SKU-Level Forecast (Next 3 Months)
        </h3>
        <div className="overflow-x-auto" data-id="element-9178">
          <table className="w-full" data-id="element-9179">
            <thead data-id="element-9180">
              <tr className="border-b-2 border-gray-300" data-id="element-9181">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9182">
                  SKU
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9183">
                  Category
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9184">
                  Oct
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9185">
                  Nov
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9186">
                  Dec
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9187">
                  Q4 Total
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9188">
                  Confidence
                </th>
              </tr>
            </thead>
            <tbody data-id="element-9189">
              {skuForecastData.map((item, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-9190">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-9191">
                    {item.sku}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9192">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(item.category)}`} data-id="element-9193">
                      {item.category}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-9194">
                    ₹{item.oct} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-9195">
                    ₹{item.nov} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-9196">
                    ₹{item.dec} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-bold text-gray-900" data-id="element-9197">
                    ₹{item.q4Total} Cr
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9198">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getConfidenceColor(item.confidence)}`} data-id="element-9199">
                      {item.confidence}
                    </span>
                  </td>
                </tr>)}
            </tbody>
            <tfoot data-id="element-9200">
              <tr className="border-t-2 border-gray-300 bg-gray-50" data-id="element-9201">
                <td colSpan={2} className="py-3 px-4 text-sm font-bold text-gray-900" data-id="element-9202">
                  Total Forecast
                </td>
                <td className="py-3 px-4 text-sm text-right font-bold text-gray-900" data-id="element-9203">
                  ₹463 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right font-bold text-gray-900" data-id="element-9204">
                  ₹485 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right font-bold text-gray-900" data-id="element-9205">
                  ₹505 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right font-bold text-blue-600" data-id="element-9206">
                  ₹1,453 Cr
                </td>
                <td className="py-3 px-4" data-id="element-9207" />
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-9208">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-9209">
            <div className="text-center" data-id="element-9210">
              <div className="text-gray-600" data-id="element-9211">High Confidence SKUs</div>
              <div className="text-xl font-bold text-green-600" data-id="element-9212">5</div>
            </div>
            <div className="text-center" data-id="element-9213">
              <div className="text-gray-600" data-id="element-9214">Medium Confidence SKUs</div>
              <div className="text-xl font-bold text-yellow-600" data-id="element-9215">3</div>
            </div>
            <div className="text-center" data-id="element-9216">
              <div className="text-gray-600" data-id="element-9217">Avg Growth Rate</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-9218">8.2%</div>
            </div>
            <div className="text-center" data-id="element-9219">
              <div className="text-gray-600" data-id="element-9220">Forecast Horizon</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-9221">3 Months</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default SalesForecastingDashboard;